/**
 * V2 app chrome — the trifecta as three plain words (decisions log):
 * Discover (home), Log a yoink (the single leather primary action),
 * My journal (your profile). Following is a toggle inside Discover,
 * never a destination here.
 *
 * Signed out, "Log a yoink" still shows (it's the app's front door) and
 * leads to the sign-in dialog via the log view's gate; My journal waits
 * for a profile to point at.
 */
import { escapeHtml } from '../../assets/js/utils.js';
import { openTellUs } from './tell-us.js';
import {
  markComplete, revealChecklist, tutorialPending, onProgressChange,
  completedCount, STEPS,
} from '../features/community/tutorial.js';

export function mount(el, ctx) {
  const { store, router } = ctx;

  function render(state) {
    // The Getting Started card stopped opening Discover uninvited
    // (2026-07-27) — a lit Help button is the whole invitation now, so it
    // has to stay lit until the twelfth step lands.
    const pending = tutorialPending();
    const username = state.profile?.username;
    const isAdmin = state.profile?.role === 'admin';
    // Home-Screen (standalone) mode has no browser chrome — V1 kept a
    // header Back button for it (app.js:546-548)
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
    el.innerHTML = `
      ${standalone ? '<button type="button" class="v2-nav-link" data-nav="back" aria-label="Back">←</button>' : ''}
      <a class="v2-nav-link" href="${router.generate('discover')}" data-nav="discover">Discover</a>
      <a class="v2-btn-leather" href="${router.generate('log')}" data-nav="log">Log a yoink</a>
      ${username ? `
      <a class="v2-nav-link" href="${router.generate('user', { username })}"
        data-nav="journal" data-nav-username="${escapeHtml(username)}">My journal</a>` : ''}
      ${isAdmin ? `
      <a class="v2-nav-link" href="${router.generate('admin')}" data-nav="admin"
        title="Ranger station — the V2 admin console">Admin</a>` : ''}
      <button type="button" class="v2-nav-link v2-nav-help${pending ? ' nav-attention' : ''}"
        data-nav="help">Help${pending ? `
        <span class="nav-dot" aria-hidden="true"></span>
        <span class="visually-hidden">— getting started, ${completedCount()} of ${STEPS.length} steps done</span>` : ''}</button>
      <dialog class="v2-auth-dialog" data-help-dialog>
        <h2 class="v2-dialog-title">Help</h2>
        <h3 class="help-subhead">Learn the ropes</h3>
        <div class="help-links">
          <button type="button" data-nav="tutorial">Getting started — the lessons</button>
          <button type="button" data-nav="glossary">What’s what — Yoinks, categories &amp; lists</button>
          <a href="https://yoinkadventures.com/how-it-works/" target="_blank" rel="noopener">How it works</a>
          <a href="https://youtube.com/playlist?list=PLFOghxkTzW32ee0_RxD-B27M3dLREuiQ7&si=L4e4DimXCyZh8f8T" target="_blank" rel="noopener">Tutorial videos</a>
          <a href="https://yoinkadventures.com" target="_blank" rel="noopener">Splash Page</a>
        </div>
        <h3 class="help-subhead">Set things up</h3>
        <div class="help-links">
          <button type="button" data-nav="import">Bring your places to Yoink</button>
          <button type="button" data-nav="phone">Put Yoink on your phone</button>
        </div>
        <h3 class="help-subhead">Say hello</h3>
        <div class="help-links">
          <button type="button" data-nav="tell-us">Tell us something — send a message</button>
          <a href="mailto:giro@yoinkadventures.com">giro@yoinkadventures.com</a>
          <a href="https://instagram.com/yoinkadventures" target="_blank" rel="noopener">Instagram @yoinkadventures</a>
          <a href="https://www.tiktok.com/@yoink.adventures" target="_blank" rel="noopener">TikTok @yoink.adventures</a>
          <a href="https://reddit.com/r/yoinkadventures" target="_blank" rel="noopener">Reddit r/yoinkadventures</a>
        </div>
        <button type="button" class="v2-btn-dialog-close" data-nav="help-close">Close</button>
      </dialog>
    `;
  }

  function onClick(event) {
    const link = event.target.closest('[data-nav]');
    if (!link) return;
    event.preventDefault();
    const nav = link.dataset.nav;
    if (nav === 'back') {
      window.history.back();
      return;
    }
    if (nav === 'discover') {
      // V1's logo tap is the nuclear reset (app.js:521-543): home with
      // filters cleared. The geographic scope is a mode, not a filter —
      // it stays (V1 kept bounds too).
      store.set({ categoryFilter: null, searchQuery: '' });
      router.navigate('discover');
    }
    else if (nav === 'log') {
      // V1 parity (app.js:774-789): logging while viewing a category
      // carries that category into the form — the same way a place
      // page carries its location. The form re-checks taggability.
      // Your OWN personal-category page carries the same way (§11) —
      // someone else's doesn't; their shelf isn't a filing you can pick.
      const current = router.parse?.();
      const me = store.get().profile?.username?.toLowerCase();
      if (current?.name === 'category' && current.params.slug) {
        router.navigate('log', {}, { query: { category: current.params.slug } });
      } else if (current?.name === 'personal-category' && current.params.slug
          && me && current.params.username?.toLowerCase() === me) {
        router.navigate('log', {}, { query: { personal: current.params.slug } });
      } else {
        router.navigate('log');
      }
    }
    else if (nav === 'admin') router.navigate('admin');
    else if (nav === 'journal') router.navigate('user', { username: link.dataset.navUsername });
    else if (nav === 'help') el.querySelector('[data-help-dialog]')?.showModal();
    else if (nav === 'tell-us') {
      // The always-reachable door to the Tell Us box. The footer carries
      // the same button, but Discover's infinite scroll means the end of
      // the document is not somewhere you can get to on the feed — and
      // the feed is where people are when they have something to say.
      el.querySelector('[data-help-dialog]')?.close();
      openTellUs({ store, router });
    }
    else if (nav === 'help-close') el.querySelector('[data-help-dialog]')?.close();
    else if (nav === 'phone' || nav === 'import' || nav === 'glossary') {
      el.querySelector('[data-help-dialog]')?.close();
      router.navigate(nav);
    }
    else if (nav === 'tutorial') {
      // The ONLY door to the checklist since 2026-07-27: it no longer
      // opens Discover on its own. Reveal it (even when complete — V1's
      // modal let finished users re-learn lessons) and go where it lives.
      el.querySelector('[data-help-dialog]')?.close();
      markComplete('open_how_it_works');
      revealChecklist();
      store.set({ categoryFilter: null, searchQuery: '', feedScope: 'all' });
      router.navigate('discover');
    }
  }

  el.addEventListener('click', onClick);
  const unsubscribe = store.subscribe(render);
  // Completing a step has to un-light Help without a navigation
  const unsubscribeProgress = onProgressChange(() => render(store.get()));
  render(store.get());

  return () => {
    el.removeEventListener('click', onClick);
    unsubscribe();
    unsubscribeProgress();
  };
}
